import { useRef, useState } from "react";
import { toast } from "sonner";

import {
  type CarrierTrip,
  useAssignOperatorToTrip,
  useCarrierOperators,
} from "@/api/carrier";
import { Button } from "@/components/ui/button";
import { Dialog, type DialogProps } from "@/components/ui/dialog";
import { NativeSelect } from "@/components/ui/native-select";
import { hasHttpStatus } from "@/lib/api-error";
import { operatorDisplayName } from "@/lib/operator-identity";

interface AssignOperatorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trip: Pick<CarrierTrip, "id" | "dispatchCenterId">;
  dialogPresentation?: DialogProps["presentation"];
  responsiveSheetAt?: DialogProps["responsiveSheetAt"];
}

/**
 * Pick an operator and POST `/trips/:id/assign-operator`. Operator options
 * come from the same scoped GET the Operators screen uses, so a dispatcher
 * only ever sees operators in their own scope.
 */
export function AssignOperatorDialog({
  open,
  onOpenChange,
  trip,
  dialogPresentation,
  responsiveSheetAt,
}: AssignOperatorDialogProps) {
  const {
    data: operators,
    isLoading,
    error: operatorsError,
  } = useCarrierOperators({ limit: 100 });
  const assign = useAssignOperatorToTrip();
  const scopedOperators = (operators?.data ?? []).filter(
    (operator) => operator.dispatchCenterId === trip.dispatchCenterId
  );
  const [operatorId, setOperatorId] = useState<string>("");
  const submitLock = useRef(false);
  const responsive = dialogPresentation === "responsive-sheet";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!operatorId || submitLock.current) return;
    submitLock.current = true;
    try {
      await assign.mutateAsync({ tripId: trip.id, operatorId });
      toast.success("Operator assigned to trip.");
      onOpenChange(false);
      setOperatorId("");
    } catch {
      toast.error("Failed to assign operator.");
      // also surfaced via assign.isError below
    } finally {
      submitLock.current = false;
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!assign.isPending) onOpenChange(nextOpen);
      }}
      title="Assign operator"
      presentation={dialogPresentation}
      responsiveSheetAt={responsiveSheetAt}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label
            htmlFor="operator-picker"
            className="block text-sm font-medium mb-1"
          >
            Operator
          </label>
          <NativeSelect
            id="operator-picker"
            value={operatorId}
            onChange={(e) => setOperatorId(e.target.value)}
            disabled={isLoading || assign.isPending}
          >
            <OperatorOptions operators={scopedOperators} />
          </NativeSelect>
        </div>

        {operatorsError && (
          <p role="alert" className="text-sm text-destructive">
            {hasHttpStatus(operatorsError, 403)
              ? "You do not have permission to load operators."
              : "Failed to load operators. Try again."}
          </p>
        )}

        {!isLoading && !operatorsError && scopedOperators.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No operators are available at this Dispatch Center.
          </p>
        )}

        {assign.isError && (
          <p role="alert" className="text-sm text-destructive">
            Failed to assign operator. Try again.
          </p>
        )}

        <div
          className={
            responsive
              ? "sticky bottom-0 grid grid-cols-2 gap-2 border-t bg-background pt-4"
              : "flex justify-end gap-2"
          }
        >
          <Button
            type="button"
            variant="outline"
            size={responsive ? "touch" : "default"}
            onClick={() => onOpenChange(false)}
            disabled={assign.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            size={responsive ? "touch" : "default"}
            disabled={!operatorId || assign.isPending}
          >
            {assign.isPending ? "Assigning..." : "Assign"}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function OperatorOptions({
  operators,
}: {
  operators: NonNullable<
    ReturnType<typeof useCarrierOperators>["data"]
  >["data"];
}) {
  return (
    <>
      <option value="">Select operator...</option>
      {operators.map((operator) => {
        const name = operatorDisplayName(operator);
        return (
          <option key={operator.id} value={operator.id}>
            {name} {operator.depot?.name ? `— ${operator.depot.name}` : ""}
          </option>
        );
      })}
    </>
  );
}
